/**
=========================================================
* Trust Cargo - "add" form definitions
=========================================================

* Each add form used to be its own page with its own copy of the same
* fetch/validate/submit code. They are described here instead, and AddDialog
* renders them - so a new field is one line, not a new screen.
*
* `optionsFrom` lets a select pull its choices from an endpoint at open time,
* which is what the old "Choose User" dropdown did by hand.
*/

import axios from "axios";
import ensureSupplierForShopCode from "utils/suppliers";
import PAY_TYPES from "utils/payType";

const API = "https://trust-cargo.co/anas_backend/api";

/**
 * The last shop code a customer was actually given - what the order form
 * fills in when you pick them.
 *
 * The server sends `last_shop_code` directly. Older deployments only send
 * `next_shop_code`, and the last one is simply that minus one: HIY06 -> HIY05.
 * A next of 01 means the customer has no orders at all, so the bare code is
 * the right answer - HIY, and whoever is typing adds the number.
 *
 * Written to survive either response so the field never comes up empty while
 * a backend change is waiting to be deployed.
 */
export function lastShopCode(customer) {
  if (!customer) return "";
  if (customer.last_shop_code) return customer.last_shop_code;

  const parts = String(customer.next_shop_code || "").match(/^(.*?)(\d+)$/);
  if (!parts) return customer.code || "";

  const [, prefix, digits] = parts;
  const previous = parseInt(digits, 10) - 1;

  // nothing used yet - fall back to the plain three letters
  if (previous < 1) return customer.code || prefix;

  return prefix + String(previous).padStart(digits.length, "0");
}

/**
 * An account is reached by its phone number and its password - there is no
 * email to ask for. The server has taken a phone as the login for a while;
 * the forms were the last place still asking for the other thing.
 */

/** A refusal shaped the way the API's own are, so the form marks the box it is about. */
function refuse(field, message) {
  const error = new Error(message);
  error.response = { data: { message, errors: { [field]: [message] } } };
  return error;
}

/** A phone number down to its digits, so "0599 123 456" and "0599123456" are one number. */
const digits = (value) => String(value ?? "").replace(/\D/g, "");

async function readList(url, key) {
  const res = await fetch(url, { cache: "no-store" });
  const json = await res.json();

  return (key ? json?.[key] : json) || [];
}

/**
 * Make the login - or hand back the one a failed attempt left behind.
 *
 * The phone number is the login now, so it may not belong to two accounts:
 * whoever typed it could be let into either. One that is already taken is
 * refused on the Mobile box, with one exception - a customer account that has
 * no customer record yet. That is what a save leaves behind when the account
 * went through and the customer did not, and registering again would make a
 * second account for the same person. It is picked up and finished instead.
 *
 * /register answers with a token rather than the row it made, so the new
 * account is found by reading the list back - on the phone number, taking
 * the highest id, which is the row that was just made.
 */
async function makeAccount({ name, mobile, password, role, field = "mobile" }) {
  const phone = String(mobile ?? "").trim();
  const before = await readList(`${API}/users`, "users");
  const holder = before.find((u) => digits(u.mobile) !== "" && digits(u.mobile) === digits(phone));

  if (holder) {
    const customers = role === "customer" ? await readList(`${API}/customers`, "customers") : [];
    const unfinished =
      role === "customer" &&
      holder.role_id === "customer" &&
      !customers.some((c) => Number(c.user_id) === Number(holder.id));

    if (!unfinished) {
      throw refuse(field, `${holder.name} already logs in with this phone number`);
    }

    return holder;
  }

  // no email key at all, rather than an empty one: email is unique on the
  // server, so a second account carrying "" collides with the first that did
  await axios.post(`${API}/register`, { name, mobile: phone, password, role_id: role });

  const account = (await readList(`${API}/users`, "users"))
    .filter((u) => String(u.mobile ?? "").trim() === phone)
    .sort((a, b) => Number(a.id) - Number(b.id))
    .pop();

  if (!account) {
    throw new Error(
      "The account was created but could not be found again - reload and check the list"
    );
  }

  return account;
}

/** The user form is making a customer: a new record, on the customer role, not from inside another form. */
const makingCustomer = (values, { isEdit }) =>
  !isEdit && values.role_id === "customer" && !values.account_only;

/**
 * Whether the order form shows its amounts: not when a customer_pay order is
 * being created - see the order entity. Editing always shows them.
 */
const amountsShown = (values, { isEdit }) => isEdit || values.pay_type !== "customer_pay";

const entities = {
  user: {
    title: "Add User",
    endpoint: `${API}/register`,
    /**
     * One form for everybody, staff or customer.
     *
     * It used to be two tabs - "Add user" and "Add customer" - for what the
     * office thinks of as one act: adding a person. A customer is a person
     * whose role is customer, and the role is what decides whether the
     * customer's own boxes (code, address, VAT name) are asked for. Most of
     * the people here are customers, so that is what the form opens on.
     *
     * Saving a customer makes the account and then the customer record
     * pointing at it; the phone number typed once is both the login and the
     * customer's number. Staff are the account and nothing else.
     */
    defaults: { role_id: "customer" },
    async create(values) {
      const account = await makeAccount({
        name: values.name,
        mobile: values.mobile,
        password: values.password,
        role: values.role_id,
      });

      if (values.role_id !== "customer" || values.account_only) return;

      await axios.post(`${API}/add_customer`, {
        name: values.name,
        code: values.code,
        address: values.address,
        vat_name: values.vat_name,
        phone_number: String(values.mobile ?? "").trim(),
        user_id: account.id,
      });
    },
    // the legacy API reads and writes on different URLs than it creates on
    edit: {
      show: (id) => `${API}/specefic_user/${id}`,
      unwrap: "user",
      submit: `${API}/edit_user`,
      method: "post",
      sendId: true,
      /**
       * There is no email box, but most of the accounts made before this
       * still log in with theirs - so the one a record came with goes back up
       * untouched. An account that has none must not send "": the column is
       * unique, the empty string counts as a value, and the second account
       * saved that way is refused. Absent, the server stores NULL.
       */
      prepare: (send) => {
        const out = { ...send };
        if (!String(out.email ?? "").trim()) delete out.email;
        return out;
      },
    },
    fields: [
      { name: "name", label: "Name", required: true },
      /**
       * The login. Asked for on every new account; on an edit it may stay
       * empty only for somebody who still has an email to get in with.
       */
      {
        name: "mobile",
        label: "Mobile (their login)",
        requiredWhen: (v) => !String(v.email ?? "").trim(),
        requiredMessage: "The phone number is what they log in with",
      },
      /**
       * Required on edit as well, on purpose. CustomerController::editUser
       * runs Hash::make($request->password) unconditionally, so an edit that
       * sends no password stores a hash of null and locks the person out.
       * Until that is guarded server-side, the password is set deliberately
       * every time rather than destroyed silently.
       */
      {
        name: "password",
        label: "Password (set on every save)",
        type: "password",
        required: true,
      },
      {
        name: "role_id",
        label: "Role",
        type: "select",
        required: true,
        /**
         * The value is the role's NAME, not the id of its row. Every user in
         * the database carries 'customer', 'admin' or 'employee' there, and
         * the people screen sorts staff from customers by reading it - a
         * user saved with "2" belongs to neither.
         */
        optionsFrom: { url: `${API}/roles`, path: "roles", value: "name", label: "name" },
      },
      /**
       * The customer's own record, asked for only while a customer is being
       * made. All three are required by CustomerController::addCustomer.
       * Editing one is still the "Edit customer" sheet: the record has a name
       * of its own there, which for most of the older customers is not the
       * account's, and one Name box over both would overwrite it.
       */
      { name: "code", label: "Code", showWhen: makingCustomer, requiredWhen: makingCustomer },
      { name: "address", label: "Address", showWhen: makingCustomer, requiredWhen: makingCustomer },
      {
        name: "vat_name",
        label: "Vat Name",
        showWhen: makingCustomer,
        requiredWhen: makingCustomer,
      },
    ],
  },

  customer: {
    title: "Add Customer",
    endpoint: `${API}/add_customer`,
    /**
     * The account, made here rather than on another screen.
     *
     * A customer row is only half a customer: without a user they cannot log
     * in and nothing can be addressed to them. Making that a separate errand
     * - add the user, come back, find them in a list of 114 - is what this
     * removes. Leave Choose User empty and the phone number and password on
     * this form become the login, on the customer role - nothing else is
     * asked for, and there is no email anywhere in it.
     *
     * Picking an existing user skips all of it, which is what an office does
     * for an account that was made without its customer record.
     */
    async beforeSubmit(values) {
      // the login belongs to the account, never to the customer record, so
      // it is stripped whichever way this goes
      const send = { ...values };
      delete send.password;

      if (String(values.user_id ?? "").trim()) return send;

      const account = await makeAccount({
        name: values.name,
        // the customer's own number is the account's number; there is only
        // ever one of them, and it is what they log in with
        mobile: values.phone_number,
        password: values.password,
        // the column holds the role's name, and this form only ever makes customers
        role: "customer",
        // this form's phone box, for a number somebody already logs in with
        field: "phone_number",
      });

      return { ...send, user_id: account.id };
    },
    edit: {
      show: (id) => `${API}/specefic_customer/${id}`,
      unwrap: "customer",
      submit: `${API}/edit_customer`,
      method: "post",
      sendId: true,
    },
    fields: [
      /**
       * All of these are required by CustomerController::addCustomer. They
       * were optional here, so a customer saved with any of them blank came
       * back as one red banner over six filled-in boxes.
       */
      { name: "name", label: "Name", required: true },
      { name: "code", label: "Code", required: true },
      { name: "phone_number", label: "Phone Number", required: true },
      { name: "address", label: "Address", required: true },
      { name: "vat_name", label: "Vat Name", required: true },
      /**
       * The other half of the login - the phone number above is the first.
       * Asked for only while a customer is being made and no existing user is
       * picked: it goes to /register and is never stored on the customer
       * record, so on an edit there is nothing here for it to change (the
       * account's password is on the "Edit user" sheet).
       */
      {
        name: "password",
        label: "Password (with the phone number, their login)",
        type: "password",
        showWhen: (v, { isEdit }) => !isEdit,
        requiredWhen: (v) => !String(v.user_id ?? "").trim(),
      },
      {
        name: "user_id",
        label: "Choose User - only if they already have an account",
        type: "select",
        optionsFrom: { url: `${API}/users`, path: "users", value: "id", label: "name" },
        // somebody who has no account yet is created from this box rather
        // than from another screen and a fresh start on this form
        createEntity: "user",
        /**
         * The user form makes a customer record of its own for a customer -
         * but here this form is about to make that record, so the one opened
         * from the box is told to stop at the account.
         */
        createDefaults: { role_id: "customer", account_only: true },
      },
    ],
  },

  container: {
    title: "Add Container",
    endpoint: `${API}/add_container`,
    edit: {
      show: (id) => `${API}/specefic_container_data/${id}`,
      unwrap: "container",
      // NOTE: the old edit_container page posted to /edit_customer - almost
      // certainly a copy-paste slip. This uses the container endpoint.
      submit: `${API}/edit_container`,
      method: "post",
      sendId: true,
    },
    wide: true,
    // addContainer and editContainer both read all of these; leaving a field
    // out of the form would blank it on the server on the next edit
    fields: [
      { name: "name", label: "Container Name", required: true },
      { name: "number", label: "Container Number" },
      { name: "status", label: "Status (loading / shipped / delivered)" },
      { name: "telex_status", label: "Telex (not_yet / released)" },
      { name: "loading_date", label: "Loading date", type: "date" },
      { name: "estimated_arrival_date", label: "Estimated arrival", type: "date" },
      { name: "notes", label: "Notes", multiline: true },
    ],
  },

  /**
   * The same container form, but through the endpoints that know about
   * types. Used once container_types exists on the server; before that the
   * `container` entity above is used and there is no type to choose.
   */
  container_typed: {
    title: "Add Container",
    endpoint: `${API}/v2/typed-containers`,
    wide: true,
    fields: [
      { name: "name", label: "Container Name", required: true },
      { name: "number", label: "Container Number" },
      {
        name: "container_type_id",
        label: "Type",
        type: "select",
        optionsFrom: { url: `${API}/v2/container-types`, value: "id", label: "name" },
      },
      /**
       * No status box, on the create form or the edit one. A container's
       * status is worked out from its number and its dates - planned, loading
       * once it has its number, clearing when the arrival comes, arrived when
       * it is received - and it follows them back as well: take a date off
       * and the container returns to where the rest put it. A status picked
       * here would be overwritten by the next read, so it is not offered.
       * See ContainerStatus on the server.
       */
      /**
       * Telex is not an ENUM on the server, it is a plain text column, so
       * there is no fixed list to read - the office keeps its own, and
       * `manage` says the box may write to it: a new wording is added from
       * the dropdown, an existing one renamed, a wrong one deleted, without
       * leaving this form.
       *
       * `allowNew` stays as well, so a one-off wording can still be typed
       * and saved on this container alone without joining the list.
       */
      {
        name: "telex_status",
        label: "Telex",
        type: "select",
        allowNew: true,
        optionsFrom: {
          url: `${API}/v2/container-field-options`,
          path: "telex_status",
          value: "value",
          label: "label",
        },
        manage: {
          url: `${API}/v2/container-field-options`,
          field: "telex_status",
        },
      },
      /**
       * The container's own timeline, in the order it happens: when it is
       * meant to load, when it actually loaded, when it should land, when it
       * was received, and when the port inspected it.
       */
      {
        name: "expected_loading_date",
        label: "Expected loading day",
        type: "date",
        // on an edit as well as a create: an older container that has none is
        // given one the next time anybody opens it
        required: true,
        requiredMessage: "Every container needs its expected loading day",
      },
      { name: "loading_date", label: "Loading date", type: "date" },
      {
        name: "estimated_arrival_date",
        label: "Estimated arrival",
        type: "date",
        // a container with its number is on its way - it also becomes loading,
        // whether the number arrives with a new container or on an edit
        requiredWhen: (v) => String(v.number ?? "").trim() !== "",
        requiredMessage: "Required once the container number is set",
      },
      { name: "received_date", label: "Container received", type: "date" },
      { name: "port_inspection_date", label: "Port inspection", type: "date" },
      /**
       * "طلب البوليصة والكشوفات" is three requests, sent to different people
       * on different days, so each is a switch of its own - and the notice of
       * that name is tied to them: it is due while any of the three is off,
       * says which, and goes when the last one is switched on. تم on the
       * notice switches on whatever is still off. See BolRequests on the
       * server.
       *
       * The wording is the office's own, and the group carries the notice's
       * name so the two are recognisably the same thing.
       */
      {
        name: "bol_requests",
        type: "switches",
        label: "طلب البوليصة والكشوفات",
        options: [
          { name: "bol_requested", label: "طلب البوليصة" },
          { name: "carrier_statement_requested", label: "طلب كشف شركة الشحن" },
          { name: "loading_costs_requested", label: "طلب كشف تكاليف التحميل" },
        ],
      },
      { name: "notes", label: "Notes", multiline: true },
    ],
  },

  status: {
    title: "Add Status",
    endpoint: `${API}/add_status`,
    edit: {
      show: (id) => `${API}/specefic_status/${id}`,
      unwrap: "status",
      submit: `${API}/edit_status`,
      method: "post",
      sendId: true,
    },
    fields: [{ name: "name", label: "Status Name", required: true }],
  },

  /**
   * One row on the Follow up sheet - and nothing else.
   *
   * Left out on purpose, because the system works them out:
   *   Customer Name  <- the first 3 letters of the shop code
   *   Deposit        <- deposits on the order, from both ledgers: the
   *                     customer's to us and ours to the supplier
   *   payment        <- sum of payments in the customer ledger
   *   Balance        <- final amount minus what was paid
   *   EX AMOUNT      <- mirrors Balance
   * There are no order line-items here either; this creates the order row.
   */
  order: {
    title: "Create Order",
    endpoint: `${API}/v2/orders`,
    wide: true,
    /**
     * A new order leaves a supplier of the same shop code behind. The server
     * does this too, in the same transaction; this covers servers that have
     * not been updated yet and is a no-op once they have.
     */
    afterCreate: (values) => ensureSupplierForShopCode(values.shop_code),
    fields: [
      /**
       * Picking the customer fills in the last shop code they were given, so
       * whoever is typing can see where that customer got to and take it from
       * there. A customer with no orders yet fills in just their three
       * letters (HIY), and the number is typed in.
       */
      {
        name: "customer_id",
        label: "Customer",
        type: "select",
        required: true,
        optionsFrom: {
          url: `${API}/v2/order-options`,
          path: "customers",
          value: "id",
          label: "label",
        },
        fills: { target: "shop_code", compute: lastShopCode },
      },
      { name: "shop_code", label: "Shop Code", required: true },
      { name: "order_date", label: "Order Date", type: "date" },
      { name: "goods_type", label: "Type of goods" },
      /**
       * Not asked for when a customer_pay order is created: the customer
       * settles with the supplier, and asking up front only got zeros typed
       * in. A we_pay order still takes them from the start, and every order
       * has them on the edit form, where Balance and Expected Balance are
       * worked out from them.
       */
      { name: "first_amount", label: "F Amount", type: "number", showWhen: amountsShown },
      { name: "final_amount", label: "Final Amount", type: "number", showWhen: amountsShown },
      { name: "total_cbm", label: "T.CBM", type: "number" },
      { name: "total_kg", label: "T. KG", type: "number" },
      // the cartons - the third measurement, and a column on the orders list
      { name: "total_ctn", label: "T.CTN", type: "number" },
      { name: "delivery_date", label: "D.Date", type: "date" },
      /**
       * Who settles with the supplier. Only a we_pay order raises the chase
       * and measurement notices, so an order created without this stays
       * silent - which is why the field is on the form rather than assumed.
       *
       * A we_pay order is forced to NEW on save whatever STATUS says: both
       * notices count from there, and an order that began life READY would
       * skip the workflow entirely.
       */
      {
        name: "pay_type",
        label: "Payment type",
        // two tabs rather than a dropdown: there are only ever two answers,
        // and which one an order is deserves to be readable without opening
        // anything. The colours are the ones the orders table tints with.
        type: "choice",
        // every order is one or the other - the workflow it goes on depends on it
        required: true,
        requiredMessage: "Choose Customer Pay or We Pay",
        // and at the head of the form rather than in the run of fields: it
        // decides what the order IS, and a we_pay one is put on a workflow
        // the other never sees
        atTop: true,
        options: PAY_TYPES,
      },
      /**
       * Where the order has got to. Every order starts at NEW and moves on
       * from the orders list - the STATUS column there, or the tick boxes
       * and "Set status" above it - so the create form does not offer it at
       * all: an order born READY skips the whole workflow, and the chase and
       * measurement notices count from NEW.
       *
       * `editOnly` keeps the box off the create form and `createValue` fills
       * NEW in behind it, found by name because the ids belong to the office
       * (they are rows in the status table, renameable from the Status
       * screen). If nobody has a status called NEW, nothing is sent and the
       * server's own default stands.
       */
      {
        name: "status_id",
        label: "STATUS",
        type: "select",
        editOnly: true,
        /**
         * READY: a customer_pay order has no workflow to wait on. The server
         * still starts a we_pay order at NEW whatever this sends, because
         * its chase and measurement notices count from there.
         */
        createValue: (list) => list.find((s) => /^\s*ready\s*$/i.test(String(s.name || "")))?.id,
        optionsFrom: {
          url: `${API}/v2/order-options`,
          path: "statuses",
          value: "id",
          label: "name",
        },
      },
      /**
       * Which container this order travels in - a real container, picked by
       * file number. It is not the order's state; STATUS above is. An order
       * whose container is not decided yet is left empty.
       */
      {
        name: "con_no",
        /**
         * Every NEW order says which container it goes in - we_pay as well as
         * customer_pay. It used to be asked of customer_pay only, with a
         * we_pay order given its container later; an order with nowhere to go
         * is what that left behind.
         *
         * An edit keeps the older rule. Plenty of we_pay orders already in the
         * system have no container, and demanding one there would stop
         * anything else on them being corrected.
         */
        requiredWhen: (v, { isEdit }) => !isEdit || v.pay_type === "customer_pay",
        requiredMessage: "Choose the container this order goes in",
        label: "CON NO (container)",
        type: "select",
        optionsFrom: {
          url: `${API}/v2/order-options`,
          path: "con_no",
          value: "value",
          label: "label",
          /**
           * A new order joins a container that has not loaded yet, so only
           * the PLANNED ones are offered - a container with its number has
           * loaded, and an order entered against it would be for goods that
           * are not on the ship. The server refuses it as well.
           *
           * An edit is offered them all: an order already travelling in a
           * loaded container has to be able to show, and keep, where it is.
           * So is a server from before it sent the status, rather than an
           * empty list nobody could choose from.
           */
          filter: (o, { isEdit }) => isEdit || o.status === undefined || o.status === "planned",
          // the box is required, so an empty list has to say the way out
          emptyText: "No planned container - add one on the Containers screen first",
        },
      },
      { name: "cbm_price_note", label: "CBM price" },
      /**
       * Declaration and packing list used to be asked for here. The office
       * does not fill them in, so the boxes are gone - but not the data: an
       * older order that has them keeps them. They ride back up untouched
       * with the rest of the record on an edit, and the server leaves them
       * alone when a save says nothing about them.
       */
      { name: "notes", label: "NOTE", multiline: true },
      /**
       * Separate from NOTE on purpose. NOTE is anything worth writing down;
       * this one raises a reminder on the day the container is expected to
       * load, so burying it in the general note would mean it never fires.
       */
      {
        name: "delivery_notes",
        label: "Notes on delivery (raises a reminder on loading day)",
        multiline: true,
      },
    ],
  },

  supplier: {
    title: "Add Supplier",
    endpoint: `${API}/v2/suppliers`,
    wide: true,
    fields: [
      { name: "code", label: "Code (e.g. SUP01)", required: true },
      { name: "name", label: "Supplier name", required: true },
      { name: "name_cn", label: "Chinese name" },
      { name: "contact_person", label: "Contact person" },
      { name: "phone", label: "Phone" },
      { name: "wechat", label: "WeChat" },
      { name: "email", label: "Email" },
      { name: "city", label: "City (Yiwu, Guangzhou ...)" },
      { name: "address", label: "Address" },
      { name: "currency", label: "Currency (CNY / USD)" },
      { name: "notes", label: "Notes", multiline: true },
    ],
  },

  /**
   * A purchase from one supplier for one customer. The order is optional on
   * purpose - goods are usually bought before it is decided which shipment
   * they travel in.
   */
  purchase: {
    title: "Add Purchase",
    endpoint: `${API}/v2/purchases`,
    wide: true,
    fields: [
      {
        name: "supplier_id",
        label: "Supplier",
        type: "select",
        required: true,
        // this endpoint returns a bare array, so no `path`
        optionsFrom: { url: `${API}/v2/suppliers`, value: "id", label: "name" },
      },
      {
        name: "customer_id",
        label: "Customer",
        type: "select",
        required: true,
        optionsFrom: {
          url: `${API}/v2/order-options`,
          path: "customers",
          value: "id",
          label: "label",
        },
      },
      { name: "invoice_no", label: "Supplier invoice no." },
      { name: "description", label: "Goods" },
      { name: "amount", label: "Amount", type: "number" },
      { name: "currency", label: "Currency (CNY)" },
      { name: "rate", label: "Rate to base (1 = same)", type: "number" },
      { name: "tracking_no", label: "Tracking no." },
      { name: "ctn", label: "CTN", type: "number" },
      { name: "cbm", label: "CBM", type: "number" },
      { name: "weight_kg", label: "Weight (kg)", type: "number" },
      { name: "notes", label: "Notes", multiline: true },
    ],
  },

  /**
   * Money paid out to a supplier. Three fields and nothing else - the
   * supplier, how much, and when.
   *
   * Anything extra a caller already knows (a purchase to tie it to, a
   * currency, a rate) is passed in through AddDialog's `defaults` and is
   * submitted with the rest, so the Customer Suppliers report can still open
   * this pointed at one specific invoice without the plain form growing
   * fields nobody fills in.
   */
  supplier_payment: {
    title: "Record Supplier Payment",
    endpoint: `${API}/v2/supplier-payments`,
    /**
     * Sent with every payment but never shown. The API reads `rate` directly
     * and errors with `Undefined array key "rate"` when a form leaves it out,
     * so it is always supplied. 1 means "the amount is already in the
     * accounting currency", which is what a three-field form implies.
     */
    defaults: { rate: 1, currency: "CNY" },
    fields: [
      {
        name: "supplier_id",
        label: "Supplier",
        type: "select",
        required: true,
        // `name` rather than a composed label: it is the only display field
        // the endpoint returns, and auto-created suppliers are named after
        // their shop code anyway
        optionsFrom: { url: `${API}/v2/suppliers`, value: "id", label: "name" },
      },
      { name: "amount", label: "Amount", type: "number", required: true },
      { name: "paid_at", label: "Payment date", type: "date", required: true },
    ],
  },

  post: {
    title: "Add Post",
    endpoint: `${API}/add_post`,
    // this one goes up as multipart because of the images
    multipart: true,
    edit: {
      // no `show`: /get_specefic_post/{id} throws on the server, so the row
      // from the list is passed in instead (AddDialog's `record` prop)
      submit: (id) => `${API}/edit_post/${id}`,
      method: "post",
    },
    fields: [
      { name: "body", label: "Body", required: true, multiline: true },
      { name: "image", label: "Images", type: "file", multiple: true },
    ],
  },

  message: {
    title: "Add Message",
    endpoint: `${API}/add_message`,
    edit: {
      show: (id) => `${API}/specefic_message/${id}`,
      unwrap: "message",
      submit: `${API}/edit_message`,
      method: "post",
      sendId: true,
    },
    fields: [
      { name: "body", label: "Message", required: true, multiline: true },
      {
        name: "customer_id",
        label: "Customer",
        type: "select",
        required: true,
        optionsFrom: { url: `${API}/customers`, path: "customers", value: "id", label: "name" },
      },
    ],
  },
};

export default entities;
